/**
 * <Skeleton> — placeholder for content that's loading.
 *
 * Used for: KPI cards, list rows, detail-pane fields. NOT for tables —
 * tables use <Spinner> per docs/design-system/08-modals-drawers-feedback.md.
 *
 * Variants:
 *   - 'text'   — single text line; pair with the line count via `lines`
 *   - 'rect'   — rectangular block (cards, images)
 *   - 'circle' — circular avatar / icon placeholder
 *
 * Subtle shimmer via Tailwind's `animate-pulse`. `prefers-reduced-motion`
 * users see a flat block — pulse is disabled by Tailwind's defaults.
 *
 * Spec: docs/design-system/08-modals-drawers-feedback.md.
 */

import { type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

export type SkeletonVariant = 'text' | 'rect' | 'circle';

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonVariant;
  /** Number of text lines to render (text variant only). */
  lines?: number;
  /** Width — accepts CSS values ('100%', '12rem', '60ch'). */
  width?: string | number;
  /** Height — accepts CSS values. */
  height?: string | number;
}

export function Skeleton({
  variant = 'rect',
  lines = 1,
  width,
  height,
  className,
  style,
  ...rest
}: SkeletonProps) {
  const base = 'bg-elevated animate-pulse';

  if (variant === 'text') {
    return (
      <div className={cn('flex flex-col gap-1.5', className)} {...rest}>
        {Array.from({ length: lines }).map((_, i) => (
          <div
            key={i}
            className={cn(base, 'h-3 rounded')}
            style={{
              // Last line shorter for natural-looking text fade
              width: i === lines - 1 && lines > 1 ? '60%' : (width ?? '100%'),
              height,
            }}
          />
        ))}
      </div>
    );
  }

  if (variant === 'circle') {
    return (
      <div
        className={cn(base, 'rounded-full', className)}
        style={{ width: width ?? '2.5rem', height: height ?? '2.5rem', ...style }}
        {...rest}
      />
    );
  }

  return (
    <div
      className={cn(base, 'rounded-md', className)}
      style={{ width: width ?? '100%', height: height ?? '1rem', ...style }}
      {...rest}
    />
  );
}

Skeleton.displayName = 'Skeleton';

export default Skeleton;
